import { useMemo, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { PartPicker } from './PartPicker';
import { groupPartsFromItems } from '../utils/partGroups';
import type { ToolImage } from '../types/LabelingTypes';

interface PartPickerSheetProps {
  /** The session's flat image list — grouped into parts here, so callers stay filter-agnostic. */
  items: ToolImage[];
  /** `null` means "every part", i.e. the toolbar's `seriesFilter === 'all'`. */
  selectedPartId: string | null;
  onSelect(partId: string | null): void;
}

/**
 * The Focus/Grid part filter: a compact trigger carrying the current part and its labeling
 * fraction, opening the shared `PartPicker` grid in a slide-over sheet. Picking a card both
 * reports the choice and closes the sheet, so the toolbar never keeps a stale panel open.
 */
export function PartPickerSheet({ items, selectedPartId, onSelect }: PartPickerSheetProps) {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const parts = useMemo(() => groupPartsFromItems(items), [items]);

  // The trigger shows the aggregate when every part is in play, and the selected part's own
  // counts otherwise — an unknown id (a filter left over from another session) reads as 0/0.
  const progress = useMemo(() => {
    if (selectedPartId === null) {
      return parts.reduce(
        (acc, part) => ({
          imageCount: acc.imageCount + part.imageCount,
          labeledCount: acc.labeledCount + part.labeledCount,
        }),
        { imageCount: 0, labeledCount: 0 }
      );
    }
    return (
      parts.find((part) => part.partId === selectedPartId) ?? { imageCount: 0, labeledCount: 0 }
    );
  }, [parts, selectedPartId]);

  const handleSelect = (partId: string | null) => {
    onSelect(partId);
    setPickerOpen(false);
  };

  return (
    <Sheet open={pickerOpen} onOpenChange={setPickerOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" data-testid="part-picker-trigger" className="gap-1.5">
          {selectedPartId === null
            ? t('labeling.partPicker.triggerAll')
            : t('labeling.partPicker.triggerSelected', { partId: selectedPartId })}
          <span
            data-testid="part-picker-trigger-progress"
            className="font-mono text-2xs bg-muted text-muted-foreground rounded px-1 tabular-nums"
          >
            {progress.labeledCount}/{progress.imageCount}
          </span>
          <ChevronDownIcon className="size-3.5 text-muted-foreground" aria-hidden="true" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>{t('labeling.partPicker.title')}</SheetTitle>
          <SheetDescription>{t('labeling.partPicker.hint')}</SheetDescription>
        </SheetHeader>
        <PartPicker
          parts={parts}
          selectedPartId={selectedPartId}
          onSelect={handleSelect}
          allowAll
          className="p-4"
        />
      </SheetContent>
    </Sheet>
  );
}
